/* /assets/lookbook-shoppable.js
 *
 * SHARED COMPONENT -- byte-identical between the OS and FB repos, same
 * as assets/os-bis.js. One section (sections/lookbook-shoppable.liquid),
 * one script, every feature gated by data attributes the section
 * writes from its own settings -- see that file's doc comment.
 *
 * REBUILT per "Lookbook: comprehensive review and rebuild spec.md" --
 * two structural changes from the previous version of this file:
 *   1. No more inline shop panel (openPanel/closePanel/movePanelInto
 *      Rail/movePanelBack are gone). Shopping a look always means the
 *      fullscreen viewer now; showRailLook() just shows/hides one of
 *      several pre-rendered rail-look nodes, nothing moves.
 *   2. The grid density control reuses sections/collection.liquid's
 *      own data-toggle-grid/data-grid-large/data-grid-small contract.
 *      The theme's own JS for this (assets/theme.js, `toggleSection`)
 *      only auto-registers for section type "collection", so this
 *      re-implements the same behaviour against the same markup
 *      rather than touching that registration -- see applyGridToggle
 *      below.
 *
 * Enhancement layer only. The grid, every look's image and link, the
 * H1, the intro and every per-look paragraph all work with this file
 * absent. This file adds: the fullscreen viewer (and therefore all
 * shopping -- see the section's own doc comment on why there's no
 * no-JS equivalent for that any more), saving looks, the email modal,
 * hero video, and the grid control's persistence.
 *
 * FOUR GUARDS THE BUILD SPEC CALLS OUT BY NAME -- all present:
 *   - history.replaceState: try/catch (Safari throws in some
 *     sandboxed contexts; unhandled it would kill every look click)
 *   - localStorage: try/catch on every read and write (Safari blocks
 *     it in private browsing and throws rather than returning null)
 *   - navigator.connection: existence-checked before use (absent in
 *     Safari and Firefox entirely)
 *   - video.play(): promise caught (iOS Low Power Mode refuses muted
 *     autoplay; falls back to the poster and a Play button)
 *
 * FOCUS TRAP: not present in the reference mockup -- the brief is
 * explicit this build must add it. theme.js has its own trapFocus,
 * but it is module-private (never exposed on window), so this file
 * has its own small implementation rather than reaching into another
 * script's closure.
 */
(function () {
  'use strict';

  var ROOT_SELECTOR = '[data-lookbook]';
  var root = document.querySelector(ROOT_SELECTOR);
  if (!root) return;

  /* ---------- storage guards ---------- */
  function storageGet(key) {
    try {
      var raw = localStorage.getItem(key);
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  }
  function storageSet(key, value) {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch (e) {
      /* Safari private browsing: fail quietly, per the brief */
    }
  }

  /* ---------- config from the section's own data attributes ---------- */
  var cfg = {
    handle: root.dataset.lookbookHandle || '',
    mobileLayout: root.dataset.mobileLayout || 'grid',
    enableShopPanel: root.dataset.enableShopPanel === 'true',
    enableFullscreen: root.dataset.enableFullscreen === 'true',
    fullscreenProductsDefault: root.dataset.fullscreenProductsDefault === 'true',
    enableSlideshow: root.dataset.enableSlideshow === 'true',
    slideshowInterval: parseFloat(root.dataset.slideshowInterval) || 3.2,
    enableSave: root.dataset.enableSave === 'true',
    enableEmailCapture: root.dataset.enableEmailCapture === 'true',
    enableDateField: root.dataset.enableDateField === 'true',
    viewerTheme: root.dataset.viewerTheme || 'dark'
  };

  var MOBILE_BREAKPOINT = 749;
  function isMobile() {
    return window.innerWidth <= MOBILE_BREAKPOINT;
  }
  function debounce(fn, wait) {
    var t;
    return function () {
      clearTimeout(t);
      var args = arguments;
      t = setTimeout(function () {
        fn.apply(null, args);
      }, wait);
    };
  }

  /* ---------- history.replaceState guard ---------- */
  function syncHash(lookNumber) {
    try {
      var hash = lookNumber ? '#look-' + lookNumber : '';
      history.replaceState(null, '', location.pathname + location.search + hash);
    } catch (e) {
      /* Safari throws on file:// and in some sandboxed contexts */
    }
  }

  /* ==================================================================
     GRID DENSITY CONTROL -- same data-toggle-grid/data-grid-large/
     data-grid-small contract as sections/collection.liquid (CSS for
     the actual column counts and per-breakpoint button visibility
     already lives sitewide in assets/theme.css). Adds one thing the
     theme's own version of this control doesn't have: persistence
     across a visit, an already-established feature of this control
     before this rebuild.
     ================================================================== */
  var grid = root.querySelector('[data-lookbook-grid]');
  var gridToggleWrap = root.querySelector('[data-lookbook-grid-toggle]');
  var gridToggleBtns = root.querySelectorAll('[data-toggle-grid]');
  var STORAGE_COLS = 'lookbook_cols_' + (cfg.handle || 'default');

  function applyGridToggle(clickedValue) {
    if (!grid) return;
    var mobile = isMobile();
    var attr = mobile ? 'data-grid-small' : 'data-grid-large';
    if (clickedValue) {
      grid.setAttribute(attr, String(clickedValue));
      storageSet(STORAGE_COLS, { mobile: mobile, value: clickedValue });
    } else {
      var stored = storageGet(STORAGE_COLS);
      if (stored && stored.mobile === mobile && stored.value) {
        grid.setAttribute(attr, String(stored.value));
      }
    }
    var current = grid.getAttribute(attr);
    gridToggleBtns.forEach(function (btn) {
      btn.classList.toggle('is-active', btn.getAttribute('data-toggle-grid') === current);
    });
  }
  if (gridToggleBtns.length) {
    gridToggleBtns.forEach(function (btn) {
      btn.addEventListener('click', function () {
        applyGridToggle(btn.getAttribute('data-toggle-grid'));
      });
    });
    applyGridToggle();
    window.addEventListener('resize', debounce(function () { applyGridToggle(); }, 150));
  }

  /* ==================================================================
     MOBILE SWIPE LAYOUT
     ================================================================== */
  var dotsEl = root.querySelector('[data-lookbook-swipe-dots]');
  var swipePrevBtn = root.querySelector('[data-lookbook-swipe-prev]');
  var swipeNextBtn = root.querySelector('[data-lookbook-swipe-next]');
  function applySwipe() {
    var swipe = cfg.mobileLayout === 'swipe' && isMobile();
    if (!grid) return;
    grid.classList.toggle('is-swipe', swipe);
    // Column density is a grid-mode concept -- swipe shows one look at a
    // time, so the control has nothing to control there.
    if (gridToggleWrap) gridToggleWrap.hidden = swipe;
    if (dotsEl) {
      dotsEl.hidden = !swipe;
      if (swipe && !dotsEl.childElementCount) {
        var looks = grid.querySelectorAll('.look');
        var frag = document.createDocumentFragment();
        looks.forEach(function (_, i) {
          var i2 = document.createElement('i');
          if (i === 0) i2.classList.add('is-on');
          frag.appendChild(i2);
        });
        dotsEl.appendChild(frag);
      }
    }
    if (swipePrevBtn) swipePrevBtn.hidden = !swipe;
    if (swipeNextBtn) swipeNextBtn.hidden = !swipe;
    if (swipe) {
      grid.addEventListener('scroll', debounce(function () {
        var looks = grid.querySelectorAll('.look');
        if (!looks.length) return;
        var idx = Math.round(grid.scrollLeft / lookStepWidth());
        var dots = dotsEl ? dotsEl.querySelectorAll('i') : [];
        dots.forEach(function (d, n) {
          d.classList.toggle('is-on', n === idx);
        });
      }, 80));
    }
  }
  // Measured from a single look tile, not grid.scrollWidth / look count --
  // the capture band and editorial blocks are also flex children of this
  // same row (rendered inline between looks, same as grid mode), so
  // scrollWidth includes their width while the look count doesn't, which
  // was throwing every step after the band's position off (measured live:
  // one tap of "prev" moved two dots, not one). Those blocks are hidden in
  // swipe mode entirely (CSS, .is-swipe .lookbook-band/.lookbook-editorial)
  // for the same reason: a promotional card competing for a slide in a
  // one-look-per-dot carousel doesn't work the way it does as a pause
  // point in a browsing grid.
  function lookStepWidth() {
    var firstLook = grid.querySelector('.look');
    if (!firstLook) return 0;
    var gap = parseFloat(getComputedStyle(grid).columnGap) || 0;
    return firstLook.getBoundingClientRect().width + gap;
  }
  function swipeStep(delta) {
    if (!grid) return;
    var width = lookStepWidth();
    if (!width) return;
    grid.scrollBy({ left: width * delta, behavior: 'smooth' });
  }
  if (swipePrevBtn) swipePrevBtn.addEventListener('click', function () { swipeStep(-1); });
  if (swipeNextBtn) swipeNextBtn.addEventListener('click', function () { swipeStep(1); });
  applySwipe();
  window.addEventListener('resize', debounce(applySwipe, 150));

  /* ==================================================================
     RAIL CART -- snippets/lookbook-rail-card.liquid, option 1 (§4a).
     Each row is a real {% form 'product' %}; chips just swap which
     variant its hidden id input points at before the browser's own
     submit does the rest (whatever theme.js already does for
     [data-add-to-cart] elsewhere -- not re-implemented here). This
     layer's own job is three things: chip selection, the cart
     attribute (Shopify attribution, §A3), and "Add the look"'s bulk
     request, which single-row Adds don't need.
     ================================================================== */
  var LOOKBOOK_ATTR_URL = '/cart/update.js';
  var lookbookCartAttrSet = false;
  function setLookbookCartAttribute() {
    if (lookbookCartAttrSet) return;
    lookbookCartAttrSet = true;
    fetch(LOOKBOOK_ATTR_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      keepalive: true,
      body: JSON.stringify({ attributes: { lookbook: cfg.handle } })
    }).catch(function () {
      lookbookCartAttrSet = false; // allow a retry on the next add
    });
  }

  // Chip click: selects a variant for that row -- the whole point of
  // option 1 is that this is the only interaction a size choice needs.
  root.querySelectorAll('[data-lookbook-rail-chip]').forEach(function (chip) {
    chip.addEventListener('click', function () {
      var row = chip.closest('[data-lookbook-rail-card]');
      if (!row) return;
      row.querySelectorAll('[data-lookbook-rail-chip]').forEach(function (c) {
        c.classList.toggle('is-selected', c === chip);
      });
      var input = row.querySelector('[data-lookbook-variant-input]');
      if (input) input.value = chip.dataset.variantId;
      var addBtn = row.querySelector('[data-lookbook-rail-add]');
      if (addBtn) addBtn.disabled = false;
    });
  });

  // Every rail form fires the cart attribute alongside whatever
  // sitewide handler processes [data-add-to-cart] -- fire-and-forget,
  // never blocks or intercepts the actual add. The brief loading/added
  // state on the button is cosmetic only (a timeout, not a real
  // response callback) -- the actual add-to-cart request and its
  // result belong entirely to that sitewide handler, not this file.
  root.querySelectorAll('[data-lookbook-rail-form]').forEach(function (form) {
    form.addEventListener('submit', function () {
      setLookbookCartAttribute();
      var addBtn = form.querySelector('[data-lookbook-rail-add]');
      if (!addBtn) return;
      addBtn.classList.add('is-loading');
      setTimeout(function () {
        addBtn.classList.remove('is-loading');
        addBtn.classList.add('is-added');
        setTimeout(function () { addBtn.classList.remove('is-added'); }, 1600);
      }, 500);
    });
  });

  // "Add the look" -- bulk add every in-stock row in this look in one
  // request. Made-to-order cards have no [data-lookbook-rail-form], so
  // they're excluded by the selector itself, not a separate check.
  root.querySelectorAll('[data-lookbook-add-look]').forEach(function (btn) {
    btn.addEventListener('click', function () {
      var lookIndex = btn.dataset.lookbookAddLook;
      var railLook = root.querySelector('[data-lookbook-rail-look="' + lookIndex + '"]');
      if (!railLook) return;
      var items = [];
      railLook.querySelectorAll('[data-lookbook-rail-form]').forEach(function (form) {
        var input = form.querySelector('[data-lookbook-variant-input]');
        if (!input || input.disabled || !input.value) return;
        items.push({
          id: input.value,
          quantity: 1,
          properties: {
            _lookbook: cfg.handle,
            _lookbook_look: String(parseInt(lookIndex, 10) + 1)
          }
        });
      });
      if (!items.length) return;
      btn.disabled = true;
      var originalText = btn.textContent;
      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: items })
      })
        .then(function (response) {
          if (!response.ok) throw new Error('add failed');
          setLookbookCartAttribute();
          btn.textContent = root.dataset.i18nAddedToBasket || originalText;
          document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
          setTimeout(function () {
            btn.textContent = originalText;
            btn.disabled = false;
          }, 2200);
        })
        .catch(function () {
          btn.disabled = false;
        });
    });
  });

  /* ==================================================================
     SAVE A LOOK
     ================================================================== */
  var STORAGE_SAVED = 'lookbook_saved_' + (cfg.handle || 'default');
  var saved = storageGet(STORAGE_SAVED) || [];
  var savedChip = root.querySelector('[data-lookbook-saved-chip]');

  function isSaved(index) {
    return saved.indexOf(String(index)) !== -1;
  }
  // Rendered server-side via the 'lookbook.saved.count' locale key
  // (one/other forms), so this stays translated rather than hardcoding
  // English -- the JS only ever swaps the digit into the "other" form.
  var i18nSavedOne = root.dataset.i18nSavedCountOne || '';
  var i18nSavedOtherTemplate = root.dataset.i18nSavedCountOther || '';
  var i18nSaveLook = root.dataset.i18nSaveLook || '';
  var i18nRemoveSaved = root.dataset.i18nRemoveSaved || '';
  function savedCountLabel(count) {
    if (count === 1 && i18nSavedOne) return i18nSavedOne;
    // 999999 is the section's own distinctive placeholder count (see
    // the data attribute in sections/lookbook-shoppable.liquid) --
    // chosen specifically so it can't collide with real string content.
    return i18nSavedOtherTemplate.replace('999999', String(count));
  }
  function paintSavedChip() {
    if (!savedChip) return;
    savedChip.hidden = saved.length === 0;
    var countEl = savedChip.querySelector('[data-lookbook-saved-count]');
    if (countEl) {
      countEl.textContent = savedCountLabel(saved.length);
    }
  }
  function paintSaveButtons() {
    root.querySelectorAll('[data-lookbook-save]').forEach(function (btn) {
      var index = btn.dataset.lookbookSave;
      var on = isSaved(index);
      btn.setAttribute('aria-pressed', on ? 'true' : 'false');
      if (i18nSaveLook && i18nRemoveSaved) {
        btn.setAttribute('aria-label', on ? i18nRemoveSaved : i18nSaveLook);
      }
      var look = btn.closest('.look');
      if (look) look.classList.toggle('is-saved', on);
    });
    var viewerSaveBtn = document.querySelector('[data-lookbook-viewer-save]');
    if (viewerSaveBtn && currentViewerIndex !== null) {
      viewerSaveBtn.setAttribute('aria-pressed', isSaved(currentViewerIndex) ? 'true' : 'false');
      viewerSaveBtn.classList.toggle('is-on', isSaved(currentViewerIndex));
    }
  }
  function toggleSave(index) {
    index = String(index);
    var pos = saved.indexOf(index);
    if (pos === -1) {
      saved.push(index);
    } else {
      saved.splice(pos, 1);
    }
    storageSet(STORAGE_SAVED, saved);
    paintSavedChip();
    paintSaveButtons();
  }
  if (cfg.enableSave) {
    root.querySelectorAll('[data-lookbook-save]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        toggleSave(btn.dataset.lookbookSave);
      });
    });
    var viewerSaveBtnInit = document.querySelector('[data-lookbook-viewer-save]');
    if (viewerSaveBtnInit) {
      viewerSaveBtnInit.addEventListener('click', function () {
        if (currentViewerIndex !== null) toggleSave(currentViewerIndex);
      });
    }
    paintSavedChip();
    paintSaveButtons();
  }

  /* ==================================================================
     FULLSCREEN VIEWER
     The only place a look's products ever render now -- see the
     section's own doc comment. showRailLook() shows/hides one of
     several pre-rendered snippets/lookbook-rail-look.liquid nodes;
     nothing moves between two surfaces any more.
     ================================================================== */
  var lb = document.getElementById('lookbook-viewer');
  var currentViewerIndex = null;
  var lastFocusedTile = null;
  var slideTimer = null;

  function lookImages(index) {
    var look = root.querySelector('.look[data-lookbook-look="' + index + '"]');
    if (!look) return [];
    var img = look.querySelector('.look__image');
    return img ? [{ src: img.currentSrc || img.src, alt: img.alt }] : [];
  }
  function totalLooks() {
    return root.querySelectorAll('.look[data-lookbook-look]').length;
  }
  function showRailLook(index) {
    if (!lb) return;
    lb.querySelectorAll('[data-lookbook-rail-look]').forEach(function (panel) {
      panel.hidden = panel.dataset.lookbookRailLook !== String(index);
    });
  }

  function paintViewer(index) {
    currentViewerIndex = index;
    var imgs = lookImages(index);
    var imgEl = lb.querySelector('[data-lookbook-viewer-image]');
    if (imgEl && imgs[0]) {
      imgEl.src = imgs[0].src;
      imgEl.alt = imgs[0].alt;
    }
    var countEl = lb.querySelector('[data-lookbook-viewer-count]');
    if (countEl) {
      countEl.textContent = 'Look ' + (parseInt(index, 10) + 1) + ' of ' + totalLooks();
    }
    if (cfg.enableShopPanel) showRailLook(index);
    paintSaveButtons();

    // preload neighbours
    [-1, 1].forEach(function (d) {
      var n = parseInt(index, 10) + d;
      if (n < 0 || n >= totalLooks()) return;
      var neighbourImgs = lookImages(n);
      if (neighbourImgs[0]) {
        var pre = new Image();
        pre.src = neighbourImgs[0].src;
      }
    });
  }

  // forceShopOpen: true when opened via a look's own bag icon (the
  // rebuild spec's own instruction -- "the bag icon should open the
  // fullscreen viewer with the rail already showing"). Otherwise
  // falls back to fullscreen_products_default, as before.
  function openViewer(index, triggerEl, forceShopOpen) {
    if (!lb) return;
    lastFocusedTile = triggerEl || document.activeElement;
    lb.classList.add('is-on');
    var showShop = forceShopOpen || cfg.fullscreenProductsDefault;
    if (cfg.enableShopPanel && showShop) {
      lb.classList.add('is-shop-open');
    }
    document.body.classList.add('lookbook-locked');
    paintViewer(index);
    syncHash(parseInt(index, 10) + 1);
    var closeBtn = lb.querySelector('[data-lookbook-viewer-close]');
    trapFocusStart(lb, closeBtn);
    if (closeBtn) closeBtn.focus();
  }
  function closeViewer() {
    if (!lb) return;
    lb.classList.remove('is-on', 'is-shop-open');
    document.body.classList.remove('lookbook-locked');
    stopSlideshow();
    trapFocusStop();
    syncHash(null);
    if (lastFocusedTile && document.body.contains(lastFocusedTile)) {
      lastFocusedTile.focus();
    }
  }
  function stepViewer(delta) {
    if (currentViewerIndex === null) return;
    var next = (parseInt(currentViewerIndex, 10) + delta + totalLooks()) % totalLooks();
    paintViewer(next);
  }
  function stopSlideshow() {
    if (slideTimer) {
      clearInterval(slideTimer);
      slideTimer = null;
      var btn = lb.querySelector('[data-lookbook-viewer-slideshow]');
      if (btn) btn.classList.remove('is-on');
    }
  }

  if (cfg.enableFullscreen && lb) {
    // Whole-image click and the corner expand icon: respects
    // fullscreen_products_default, doesn't force the rail open.
    root.querySelectorAll('[data-lookbook-expand]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        openViewer(btn.dataset.lookbookExpand, btn);
      });
    });
    // The bag icon: always opens with the rail showing -- there is no
    // inline panel any more for it to toggle instead.
    if (cfg.enableShopPanel) {
      root.querySelectorAll('[data-lookbook-shop-toggle]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          openViewer(btn.dataset.lookbookShopToggle, btn, true);
        });
      });
    }
    var closeBtnEl = lb.querySelector('[data-lookbook-viewer-close]');
    if (closeBtnEl) closeBtnEl.addEventListener('click', closeViewer);
    var stageEl = lb.querySelector('[data-lookbook-viewer-stage]');
    if (stageEl) {
      stageEl.addEventListener('click', function (e) {
        if (!e.target.closest('[data-lookbook-viewer-image]')) closeViewer();
      });
    }
    var prevBtn = lb.querySelector('[data-lookbook-viewer-prev]');
    var nextBtn = lb.querySelector('[data-lookbook-viewer-next]');
    if (prevBtn) prevBtn.addEventListener('click', function () { stopSlideshow(); stepViewer(-1); });
    if (nextBtn) nextBtn.addEventListener('click', function () { stopSlideshow(); stepViewer(1); });

    var shopToggleBtn = lb.querySelector('[data-lookbook-viewer-shop-toggle]');
    if (shopToggleBtn) {
      shopToggleBtn.addEventListener('click', function () {
        var on = lb.classList.toggle('is-shop-open');
        var label = shopToggleBtn.querySelector('[data-lookbook-viewer-shop-label]');
        if (label) label.textContent = on ? 'Hide products' : 'Show products';
        if (on) stopSlideshow();
      });
    }
    var slideshowBtn = lb.querySelector('[data-lookbook-viewer-slideshow]');
    if (slideshowBtn && cfg.enableSlideshow) {
      slideshowBtn.addEventListener('click', function () {
        if (slideTimer) {
          stopSlideshow();
        } else {
          slideshowBtn.classList.add('is-on');
          slideTimer = setInterval(function () { stepViewer(1); }, cfg.slideshowInterval * 1000);
        }
      });
    }
    var viewerEmailBtn = lb.querySelector('[data-lookbook-viewer-email]');
    if (viewerEmailBtn && cfg.enableEmailCapture) {
      viewerEmailBtn.addEventListener('click', function () {
        openEmailModal('look', currentViewerIndex);
      });
    }

    // touch swipe
    var touchStartX = 0;
    lb.addEventListener('touchstart', function (e) {
      touchStartX = e.changedTouches[0].clientX;
    }, { passive: true });
    lb.addEventListener('touchend', function (e) {
      var dx = e.changedTouches[0].clientX - touchStartX;
      if (Math.abs(dx) > 50) {
        stopSlideshow();
        stepViewer(dx < 0 ? 1 : -1);
      }
    }, { passive: true });
  }

  /* ---------- focus trap (built here -- see the file header) ---------- */
  var trapHandlers = null;
  function focusableEls(container) {
    return Array.prototype.slice.call(
      container.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])')
    ).filter(function (el) {
      return !el.disabled && el.offsetParent !== null;
    });
  }
  function trapFocusStart(container, elementToFocus) {
    trapFocusStop();
    var keydown = function (e) {
      if (e.key !== 'Tab') return;
      var els = focusableEls(container);
      if (!els.length) return;
      var first = els[0];
      var last = els[els.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    container.addEventListener('keydown', keydown);
    trapHandlers = { container: container, keydown: keydown };
    if (elementToFocus) elementToFocus.focus();
  }
  function trapFocusStop() {
    if (trapHandlers) {
      trapHandlers.container.removeEventListener('keydown', trapHandlers.keydown);
      trapHandlers = null;
    }
  }

  /* ==================================================================
     SAVED DRAWER
     ================================================================== */
  var savedOverlay = document.getElementById('lookbook-saved-drawer');
  function renderSavedDrawer() {
    if (!savedOverlay) return;
    var body = savedOverlay.querySelector('[data-lookbook-saved-body]');
    if (!body) return;
    if (!saved.length) {
      body.innerHTML = '<p class="lookbook-saved-empty">Nothing saved yet. Use the heart on any look.</p>';
      return;
    }
    var html = '<div class="lookbook-saved-list">';
    saved.forEach(function (index) {
      var look = root.querySelector('.look[data-lookbook-look="' + index + '"] .look__image');
      var src = look ? (look.currentSrc || look.src) : '';
      var alt = look ? look.alt : '';
      html += '<figure><div class="lookbook-saved-list__thumb"><img src="' + src + '" alt="' + alt.replace(/"/g, '&quot;') + '" loading="lazy"></div>' +
        '<button type="button" data-lookbook-saved-remove="' + index + '">Remove</button></figure>';
    });
    html += '</div>';
    body.innerHTML = html;
    body.querySelectorAll('[data-lookbook-saved-remove]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        toggleSave(btn.dataset.lookbookSavedRemove);
        renderSavedDrawer();
      });
    });
  }
  function openSavedDrawer() {
    if (!savedOverlay) return;
    renderSavedDrawer();
    savedOverlay.classList.add('is-on');
    document.body.classList.add('lookbook-locked');
    var closeBtn = savedOverlay.querySelector('[data-lookbook-saved-close]');
    trapFocusStart(savedOverlay, closeBtn);
  }
  function closeSavedDrawer() {
    if (!savedOverlay) return;
    savedOverlay.classList.remove('is-on');
    if (!(lb && lb.classList.contains('is-on'))) document.body.classList.remove('lookbook-locked');
    trapFocusStop();
  }
  if (cfg.enableSave && savedChip) {
    savedChip.addEventListener('click', openSavedDrawer);
    var savedCloseBtn = savedOverlay ? savedOverlay.querySelector('[data-lookbook-saved-close]') : null;
    if (savedCloseBtn) savedCloseBtn.addEventListener('click', closeSavedDrawer);
    var savedEmailBtn = savedOverlay ? savedOverlay.querySelector('[data-lookbook-saved-email]') : null;
    if (savedEmailBtn) {
      savedEmailBtn.addEventListener('click', function () {
        closeSavedDrawer();
        openEmailModal('saved', null);
      });
    }
    if (savedOverlay) {
      savedOverlay.addEventListener('click', function (e) {
        if (e.target === savedOverlay) closeSavedDrawer();
      });
    }
  }

  /* ==================================================================
     EMAIL MODAL -- newsletter signup, wired to Klaviyo's client API
     A single plain subscribe, regardless of which of the three
     surfaces opened it (a look's "Email this look" bar button, the
     saved-looks drawer, or the in-grid capture band) -- see the
     snippet's own doc comment for why this replaced a per-look
     "we'll email you this content" promise. Occasion, when the
     merchant has it on, rides on the profile as a property and never
     blocks submission -- no promise is made off the back of it.
     ================================================================== */
  var emailOverlay = document.getElementById('lookbook-email-modal');
  var KLAVIYO_LIST_ID = 'S9Q2au';
  var KLAVIYO_REVISION = '2026-07-15'; // same pinned revision as assets/os-bis.js
  var klaviyoCompanyId = root.dataset.klaviyoPublicKey || '';

  function resetEmailModalCopy() {
    if (!emailOverlay) return;
    var titleEl = emailOverlay.querySelector('[data-lookbook-email-title]');
    var subEl = emailOverlay.querySelector('[data-lookbook-email-subtitle]');
    if (titleEl) titleEl.textContent = root.dataset.i18nEmailTitle || 'Be the first to know';
    if (subEl) subEl.textContent = root.dataset.i18nEmailSubtitle || '';
  }

  function openEmailModal(scope, arg, prefillAddress) {
    if (!emailOverlay) return;
    resetEmailModalCopy();
    var addrEl = emailOverlay.querySelector('[data-lookbook-email-address]');
    if (addrEl) addrEl.value = prefillAddress || '';
    var errEl = emailOverlay.querySelector('[data-lookbook-email-error]');
    if (errEl) errEl.hidden = true;
    var sendBtn = emailOverlay.querySelector('[data-lookbook-email-send]');
    if (sendBtn) sendBtn.disabled = false;
    emailOverlay.classList.add('is-on');
    document.body.classList.add('lookbook-locked');
    trapFocusStart(emailOverlay, addrEl);
  }
  function closeEmailModal() {
    if (!emailOverlay) return;
    emailOverlay.classList.remove('is-on');
    if (!(lb && lb.classList.contains('is-on')) && !(savedOverlay && savedOverlay.classList.contains('is-on'))) {
      document.body.classList.remove('lookbook-locked');
    }
    trapFocusStop();
  }
  if (cfg.enableEmailCapture && emailOverlay) {
    var cancelBtn = emailOverlay.querySelector('[data-lookbook-email-cancel]');
    if (cancelBtn) cancelBtn.addEventListener('click', closeEmailModal);
    emailOverlay.addEventListener('click', function (e) {
      if (e.target === emailOverlay) closeEmailModal();
    });

    var bandSubmit = root.querySelector('[data-lookbook-band-submit]');
    if (bandSubmit) {
      bandSubmit.addEventListener('click', function () {
        var input = root.querySelector('[data-lookbook-band-email]');
        openEmailModal('band', null, input ? input.value.trim() : '');
      });
    }

    var sendBtn = emailOverlay.querySelector('[data-lookbook-email-send]');
    if (sendBtn) {
      sendBtn.addEventListener('click', function () {
        var addrEl = emailOverlay.querySelector('[data-lookbook-email-address]');
        var occEl = emailOverlay.querySelector('[data-lookbook-email-occasion]');
        var errEl = emailOverlay.querySelector('[data-lookbook-email-error]');
        var address = addrEl ? addrEl.value.trim() : '';
        var emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

        if (!emailPattern.test(address)) {
          if (errEl) {
            errEl.textContent = root.dataset.i18nErrorInvalidEmail || 'Enter a valid email address';
            errEl.hidden = false;
          }
          if (addrEl) addrEl.focus();
          return;
        }
        // Occasion is optional -- it rides on the profile as a
        // property when given, never blocks the subscribe.
        if (errEl) errEl.hidden = true;
        subscribeToNewsletter(address, occEl ? occEl.value : '');
      });
    }
  }

  function subscribeToNewsletter(email, occasion) {
    var errEl = emailOverlay.querySelector('[data-lookbook-email-error]');
    var titleEl = emailOverlay.querySelector('[data-lookbook-email-title]');
    var subEl = emailOverlay.querySelector('[data-lookbook-email-subtitle]');
    var sendBtn = emailOverlay.querySelector('[data-lookbook-email-send]');
    if (sendBtn) sendBtn.disabled = true;

    var profileAttributes = {
      email: email,
      subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } }
    };
    if (occasion) {
      profileAttributes.properties = { occasion: occasion };
    }
    var body = {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: 'Lookbook',
          profile: {
            data: {
              type: 'profile',
              attributes: profileAttributes
            }
          }
        },
        relationships: {
          list: {
            data: { type: 'list', id: KLAVIYO_LIST_ID }
          }
        }
      }
    };

    fetch('https://a.klaviyo.com/client/subscriptions/?company_id=' + klaviyoCompanyId, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/vnd.api+json',
        revision: KLAVIYO_REVISION
      },
      body: JSON.stringify(body)
    })
      .then(function (response) {
        if (response.status !== 202) throw new Error('subscribe failed');
        if (titleEl) titleEl.textContent = root.dataset.i18nEmailSentTitle || "You're on the list";
        if (subEl) subEl.textContent = root.dataset.i18nEmailSentSubtitle || '';
      })
      .catch(function () {
        if (sendBtn) sendBtn.disabled = false;
        if (errEl) {
          errEl.textContent = root.dataset.i18nErrorGeneric || 'Something went wrong. Try again in a moment.';
          errEl.hidden = false;
        }
      });
  }
  // "Email this look" now lives only in the viewer's own bar (see
  // snippets/lookbook-viewer.liquid) -- bound above via
  // viewerEmailBtn, not here, since there's no per-look button
  // outside the viewer any more.

  /* ==================================================================
     KEYBOARD: Escape closes topmost surface, arrows move the viewer
     ================================================================== */
  document.addEventListener('keydown', function (e) {
    if (e.key === 'ArrowRight' && lb && lb.classList.contains('is-on')) {
      stopSlideshow();
      stepViewer(1);
      return;
    }
    if (e.key === 'ArrowLeft' && lb && lb.classList.contains('is-on')) {
      stopSlideshow();
      stepViewer(-1);
      return;
    }
    if (e.key !== 'Escape') return;
    if (emailOverlay && emailOverlay.classList.contains('is-on')) {
      closeEmailModal();
      return;
    }
    if (savedOverlay && savedOverlay.classList.contains('is-on')) {
      closeSavedDrawer();
      return;
    }
    if (lb && lb.classList.contains('is-on')) {
      closeViewer();
    }
  });

  /* ==================================================================
     DEEP LINK: #look-3 on load -- always via the viewer now (there's
     no inline panel to fall back to any more).
     ================================================================== */
  function openFromHash() {
    var m = /^#look-(\d+)$/.exec(location.hash);
    if (!m) return;
    var index = parseInt(m[1], 10) - 1;
    if (index < 0 || index >= totalLooks()) return;
    if (cfg.enableFullscreen) {
      openViewer(index, null, cfg.enableShopPanel);
    }
  }
  window.addEventListener('hashchange', openFromHash);
  openFromHash();

  /* ==================================================================
     HERO VIDEO -- all four guards live here
     ================================================================== */
  var heroSection = root.querySelector('[data-lookbook-hero]');
  var heroImageWrap = heroSection ? heroSection.querySelector('[data-lookbook-hero-image]') : null;
  var heroVideoWrap = heroSection ? heroSection.querySelector('[data-lookbook-hero-video]') : null;
  // Two <video> elements can be present (desktop film + an optional
  // portrait mobile cut, CSS shows/hides by breakpoint) -- pick the one
  // that's actually visible so playback isn't wasted on the hidden one.
  // A one-time check at load, not re-evaluated on resize, matching the
  // rest of this section's hero sizing (also decided once at render).
  var heroVideoMobile = heroVideoWrap ? heroVideoWrap.querySelector('.lookbook-shoppable__hero-video--mobile') : null;
  var heroVideo = heroVideoWrap
    ? (isMobile() && heroVideoMobile ? heroVideoMobile : heroVideoWrap.querySelector('.lookbook-shoppable__hero-video--desktop'))
    : null;
  var heroControls = heroSection ? heroSection.querySelector('[data-lookbook-hero-controls]') : null;
  var heroPlayBtn = heroSection ? heroSection.querySelector('[data-lookbook-hero-play]') : null;

  function heroBlockedReason() {
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return 'reduced-motion';
    }
    var c = navigator.connection; // absent in Safari and Firefox -- guarded
    if (c && (c.saveData || /2g/.test(c.effectiveType || ''))) {
      return 'slow-connection';
    }
    return null;
  }
  function showHeroStill() {
    if (heroVideoWrap) heroVideoWrap.hidden = true;
    if (heroVideo) heroVideo.pause();
    if (heroImageWrap) heroImageWrap.hidden = false;
    if (heroControls) heroControls.hidden = true;
  }
  function showHeroFilm() {
    if (!heroVideo) return;
    if (heroBlockedReason()) {
      showHeroStill();
      return;
    }
    if (heroImageWrap) heroImageWrap.hidden = true;
    if (heroVideoWrap) heroVideoWrap.hidden = false;
    heroVideo.preload = 'auto';
    var playPromise = heroVideo.play();
    if (playPromise && typeof playPromise.then === 'function') {
      playPromise.then(function () {
        if (heroControls) heroControls.hidden = false;
        if (heroPlayBtn) heroPlayBtn.hidden = true;
      }).catch(function () {
        // iOS Low Power Mode or similar refusal -- offer a manual Play button
        if (heroControls) heroControls.hidden = false;
        if (heroPlayBtn) heroPlayBtn.hidden = false;
      });
    }
  }
  if (heroVideo) {
    heroVideo.addEventListener('error', showHeroStill, true);
    if (heroPlayBtn) {
      heroPlayBtn.addEventListener('click', function () {
        if (heroImageWrap) heroImageWrap.hidden = true;
        if (heroVideoWrap) heroVideoWrap.hidden = false;
        heroVideo.play();
        heroPlayBtn.hidden = true;
      });
    }
    showHeroFilm();
  }
})();
